import type { Editor } from "ckeditor5"

export const UPCAST_MARKER_PREFIX = "__img:"

export const OBJECT_FIT_VALUES = ["", "fill", "contain", "cover", "none", "scale-down"] as const
export type ObjectFitValue = typeof OBJECT_FIT_VALUES[number]

export const OBJECT_FIT_LABELS: Record<ObjectFitValue, string> = {
  "": "Không đổi",
  fill: "Fill",
  contain: "Contain",
  cover: "Cover",
  none: "None",
  "scale-down": "Scale down",
}

export function hasMarkerFormat(value: string | undefined): boolean {
  return Boolean(value && typeof value === "string" && value.startsWith(UPCAST_MARKER_PREFIX))
}

export function extractObjectFitFromMarker(marker: string): string {
  return marker.replace(UPCAST_MARKER_PREFIX, "")
}

export function createMarkerValue(value: string): string {
  return UPCAST_MARKER_PREFIX + value
}

export function isImageElement(element: { name: string } | undefined | null): boolean {
  return Boolean(element && ["image", "imageInline", "imageBlock"].includes(element.name))
}

export function getObjectFitFromModel(editor: Editor, src: string): string | null {
  const root = editor.model.document.getRoot()
  if (!root) return null

  const range = editor.model.createRangeIn(root)
  for (const item of range.getItems()) {
    if (item.is("element") && isImageElement(item as any)) {
      const attrs = item.getAttributes() as Iterable<[string, any]>
      for (const [key, value] of Array.from(attrs)) {
        if (key === "objectFit" && typeof value === "string") {
          const srcAttr = (item as any).getAttribute("src")
          if (srcAttr === src) {
            return value
          }
        }
      }
    }
  }

  return null
}

export function injectObjectFitToHtml(html: string, getObjectFit: (src: string) => string | null): string {
  if (typeof window === "undefined") return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")

  const figures = doc.querySelectorAll("figure")
  figures.forEach((figure) => {
    const img = figure.querySelector("img")
    if (!img) return
    const src = img.getAttribute("src")
    if (!src) return

    const objectFit = getObjectFit(src)
    if (objectFit) {
      img.setAttribute("data-object-fit", objectFit)
      applyObjectFitStyle(img, objectFit)
    }
  })

  doc.querySelectorAll("img").forEach((img) => {
    if (img.closest("figure")) return
    const src = img.getAttribute("src")
    if (!src) return

    const objectFit = getObjectFit(src)
    if (objectFit) {
      img.setAttribute("data-object-fit", objectFit)
      applyObjectFitStyle(img, objectFit)
    }
  })

  return doc.body.innerHTML
}

export function applyObjectFitStyle(img: HTMLImageElement, objectFit: string): void {
  const currentStyle = img.getAttribute("style") || ""
  const styleWithoutObjectFit = currentStyle.replace(/object-fit\s*:\s*[^;]+;?/gi, "").trim()
  const newStyle = styleWithoutObjectFit
    ? `${styleWithoutObjectFit};object-fit:${objectFit}`
    : `object-fit:${objectFit}`
  img.setAttribute("style", newStyle)
}

export function extractObjectFitFromStyle(style: string): string | null {
  const match = style.match(/object-fit\s*:\s*([^;]+)/)
  return match ? match[1].trim() : null
}

export function createObjectFitOptions(): string {
  return OBJECT_FIT_VALUES
    .map((value) => `<option value="${value}">${OBJECT_FIT_LABELS[value]}</option>`)
    .join("")
}
